// perform-panel-commit.ts — PSTUDIO-1433
import {
  buildPanelCommitPlan,
  resolvePanelCommitAction,
  type PanelEditSession,
} from './panel-edit-session';

/**
 * The narrow slice of the table store a panel commit touches. Declared as a
 * PORT rather than importing the store so the sequence below can be tested
 * against a fake — the whole point of extracting it.
 */
export interface PanelCommitStore {
  isPanelEditRegistered(rowId: string, columnId: string): boolean;
  endPanelEdit(): void;
  readDraft(rowId: string, columnId: string): string;
  updateRow(args: {
    rowId: string;
    updates: Record<string, unknown>;
    skipValidationRules?: boolean;
    saveImmediately?: boolean;
  }): void;
  saveUnsavedChanges(): void;
  commitCellEdit(args: {
    rowId: string;
    columnId: string;
    before: Record<string, unknown>;
  }): void;
  clearRowDirty(rowId: string): void;
}

export type PanelCommitOutcome =
  | 'saved'
  | 'cleared-dirty'
  | 'reverted-layout'
  | 'no-session';

/**
 * Commit the panel's open edit session.
 *
 * Extracted from the component because the sequence itself is the thing that
 * can break, and a test that re-implements it in the test file proves nothing:
 * mutating the real component (dropping the de-registration guard, reading the
 * draft off the live selection, or never registering the session at all) left a
 * suite of 2477 tests green, because the only production code it called was the
 * pure planner. With the sequence here, a fake store pins the ORDER and the
 * calls — the four things that actually go wrong:
 *
 *  1. the session is consumed BEFORE anything else, so the incidental blur that
 *     Enter's own selection move triggers finds nothing left to commit
 *     (the ticket's "no double-save conflict");
 *  2. a session torn down while it was open (undo/redo, a delete, a coworker
 *     merge) writes NOTHING — writing anyway is how a deferred flush reverted a
 *     user's undo and pushed a history step they never made;
 *  3. the draft is read for the SESSION's cell, never through the selection,
 *     which by then already describes the next cell;
 *  4. the row's dirty flag is cleared on BOTH non-writing paths, or the next
 *     unrelated `saveUnsavedChanges()` persists the raw, uncanonicalized draft.
 */
export function performPanelCommit(
  store: PanelCommitStore,
  session: PanelEditSession | null,
  buildUpdates: (
    columnId: string,
    value: string,
    isJsonColumn: boolean
  ) => Record<string, unknown>
): PanelCommitOutcome {
  if (!session) return 'no-session';

  const { rowId: sessionRowId, columnId: sessionColumnId } = session;
  const stillRegistered = store.isPanelEditRegistered(
    sessionRowId,
    sessionColumnId
  );
  store.endPanelEdit();

  const rawDraft = store.readDraft(sessionRowId, sessionColumnId);

  // PSTUDIO-4415: the draft is the layout the editor showed, untouched. Nothing
  // is saved, and the row gets back the value (and key-order sibling) it held
  // when the edit began — the draft writes had already replaced it in memory,
  // and leaving the layout there would let the next save of anything on this
  // row persist it after all. Only while the session is still registered: a
  // torn-down session writes nothing, whatever its draft.
  if (
    stillRegistered &&
    resolvePanelCommitAction(session, rawDraft) === 'revert'
  ) {
    store.updateRow({
      rowId: sessionRowId,
      updates: session.before,
      skipValidationRules: true,
      saveImmediately: false,
    });
    store.clearRowDirty(sessionRowId);
    return 'reverted-layout';
  }

  const plan = stillRegistered ? buildPanelCommitPlan(session, rawDraft) : null;

  if (!plan) {
    store.clearRowDirty(sessionRowId);
    return 'cleared-dirty';
  }

  store.updateRow({
    rowId: plan.rowId,
    updates: buildUpdates(plan.columnId, plan.value, plan.isJsonColumn),
    skipValidationRules: true,
    saveImmediately: false,
  });
  store.saveUnsavedChanges();
  store.commitCellEdit({
    rowId: plan.rowId,
    columnId: plan.columnId,
    before: plan.before,
  });
  return 'saved';
}
